import { Component, OnInit } from '@angular/core';
import { Account } from '@contracts/interfaces/account.interface';
import { ACCOUNT } from '../../accounts';
import { AccountService } from '../../services/account.service';
import { TransactionsService } from '../../services/transactions.service';
import { Transaction, COGS, OperatingExpenses } from '@contracts/interfaces/transaction.interfaces';
import { MatTableDataSource } from '@angular/material/table';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexStroke,
  ApexDataLabels,
  ApexMarkers,
  ApexTooltip,
  ApexYAxis,
  ApexAnnotations
} from 'ng-apexcharts';

export type TrendChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  markers: ApexMarkers;
  tooltip: ApexTooltip;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  annotations: ApexAnnotations;
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {

  revenueTotal = 0;
  expenseTotal = 0;
  profit = 0;
  isLoading = true;
  isLoadingMetrics = true;
  accounts: Account[] = [];

  rollingRevenue7 = 0;
  rollingRevenue30 = 0;
  rollingRevenue90 = 0;
  rollingExpense7 = 0;
  rollingExpense30 = 0;
  rollingExpense90 = 0;
  rollingProfit7 = 0;
  rollingProfit30 = 0;
  rollingProfit90 = 0;
  rollingDesiredRevenue7 = 0;
  rollingDesiredRevenue30 = 0;
  rollingDesiredRevenue90 = 0;
  desiredProfit7 = 0;
  desiredProfit30 = 0;
  desiredProfit90 = 0;
  desiredDelta7 = 0;
  desiredDelta30 = 0;
  desiredDelta90 = 0;
  profitSlopePerDay = 0;
  avgProfitPerDay = 0;
  showRollingAverage = true;
  private appliedInitialZoom = false;
  private lastAverageSeries: { x: Date; y: number }[] = [];
  private lastTrendLine: { x: Date; y: number }[] = [];
  private lastTrendValue = 0;
  private yZoomMin = 0;
  private yZoomMax = 0;
  trendChartOptions: Partial<TrendChartOptions> = {};
  recentRevenue7: Transaction[] = [];
  recentExpensesByCategory: ExpenseCategoryRow[] = [];
  expensesInRange: Transaction[] = [];
  selectedExpenseCategory: ExpenseCategoryRow | null = null;
  selectedExpenseTransactions: Transaction[] = [];
  selectedRangeDays = 7;
  private readonly rangeOptions = [7, 30, 90];
  private readonly primaryRevenueAccount = ACCOUNT.REVENUE;

  private readonly revenueAccounts = [
    ACCOUNT.REVENUE,
    ACCOUNT.G72REVENUE,
    ACCOUNT.CASH_REVENUE,
    ACCOUNT.SQUARE_REVENUE,
    ACCOUNT.EBAY_REVENUE
  ];

  private readonly expenseAccounts = [
    ACCOUNT.EXPENSES,
    ACCOUNT.G72EXPENSES
  ];

  private readonly cogsCategories = COGS.map(c => c.value);
  private readonly opCategories = OperatingExpenses.map(c => c.value);

  constructor(
    private accountService: AccountService,
    private transactionsService: TransactionsService
  ) { }

  ngOnInit(): void {
    const stored = localStorage.getItem('dashboard.showRollingAverage');
    if (stored !== null) {
      this.showRollingAverage = stored === 'true';
    }
    this.loadProfitLoss();
    this.loadRollingMetrics();
    this.loadRangeLists(this.selectedRangeDays);
  }

  onChartWheel(event: WheelEvent) {
    if (!this.trendChartOptions) return;
    event.preventDefault();
    const factor = event.deltaY > 0 ? 1.15 : 0.85; // scroll down -> zoom out
    const center = this.lastTrendValue || (this.yZoomMin + this.yZoomMax) / 2;
    const span = (this.yZoomMax - this.yZoomMin) * factor;
    this.yZoomMin = center - span / 2;
    this.yZoomMax = center + span / 2;
    this.trendChartOptions = {
      ...this.trendChartOptions,
      yaxis: {
        ...(this.trendChartOptions.yaxis || {}),
        min: this.yZoomMin,
        max: this.yZoomMax,
        labels: {
          formatter: (val: number) => this.formatCentsToDollars(val)
        }
      }
    };
  }

  toggleRollingAverage() {
    this.showRollingAverage = !this.showRollingAverage;
    localStorage.setItem('dashboard.showRollingAverage', this.showRollingAverage ? 'true' : 'false');
    const trend = { name: 'Trend', data: this.lastTrendLine, color: '#2563eb' };
    const avg = { name: 'Avg to date', data: this.lastAverageSeries, color: '#10b981' };
    this.trendChartOptions = {
      ...this.trendChartOptions,
      series: this.showRollingAverage ? [avg, trend] : [trend],
      stroke: {
        curve: 'straight',
        width: this.showRollingAverage ? [2, 3] : [3],
        dashArray: this.showRollingAverage ? [4, 0] : [0]
      }
    };
  }

  selectExpenseCategory(cat: ExpenseCategoryRow) {
    if (!cat) return;
    this.selectedExpenseCategory = cat;
    this.selectedExpenseTransactions = (this.expensesInRange || []).filter(tx => (tx.atocategory || 'uncategorised') === cat.category);
  }

  backToExpenseCategories() {
    this.resetExpenseSelection();
  }

  private resetExpenseSelection() {
    this.selectedExpenseCategory = null;
    this.selectedExpenseTransactions = [];
  }

  zoomToFit() {
    if (!this.lastTrendLine || !this.lastTrendLine.length) return;
    // Fit to current trend data plus guide lines with modest padding
    const survival = 8500;
    const dignity = 14000;
    const trendData = this.lastTrendLine?.map(p => p.y) || [];
    const baseMin = trendData.length ? Math.min(...trendData) : 0;
    const baseMax = trendData.length ? Math.max(...trendData) : 0;
    const minY = Math.min(baseMin, survival);
    const maxY = Math.max(baseMax, dignity);
    const padding = Math.max(2000, (maxY - minY) * 0.2); // at least $20 padding
    this.yZoomMin = minY - padding;
    this.yZoomMax = maxY + padding;

    const trend = { name: 'Trend', data: this.lastTrendLine, color: '#2563eb' };
    const avg = { name: 'Avg to date', data: this.lastAverageSeries, color: '#10b981' };
    this.trendChartOptions = {
      ...this.trendChartOptions,
      series: this.showRollingAverage ? [avg, trend] : [trend],
      yaxis: {
        ...(this.trendChartOptions?.yaxis || {}),
        min: this.yZoomMin,
        max: this.yZoomMax,
        labels: {
          formatter: (val: number) => this.formatCentsToDollars(val)
        }
      }
    };
  }

  private loadProfitLoss() {
    this.isLoading = true;
    this.accountService.find().subscribe((accounts: any) => {
      this.accounts = accounts || [];

      const revenueSum = this.sumAccounts(this.revenueAccounts);
      const expenseSum = this.sumAccounts(this.expenseAccounts);

      this.revenueTotal = this.exGstAmount(revenueSum);
      this.expenseTotal = this.exGstAmount(expenseSum);

      // Expenses are usually negative; subtract absolute value to get profit.
      this.profit = this.revenueTotal - Math.abs(this.expenseTotal);
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
    });
  }

  private sumAccounts(accountNumbers: string[]): number {
    return this.accounts
      .filter((a: Account) => accountNumbers.includes(a.accountNumber))
      .reduce((sum, acc: Account) => sum + (acc?.balance || 0), 0);
  }

  private loadRollingMetrics() {
    this.isLoadingMetrics = true;
    const now = new Date();
    const sevenRange = this.getRollingRange(7, now);
    const thirtyRange = this.getRollingRange(30, now);
    const ninetyRange = this.getRollingRange(90, now);

    const commonIgnore = { $in: [null, false] };

    const revenueQuery7 = {
      "source.account": this.primaryRevenueAccount,
      ignore: commonIgnore,
      date: { $gte: sevenRange.start, $lte: sevenRange.end }
    };

    const revenueQuery30 = {
      "source.account": this.primaryRevenueAccount,
      ignore: commonIgnore,
      date: { $gte: thirtyRange.start, $lte: thirtyRange.end }
    };

    const expenseQuery90 = {
      "source.account": { $in: this.expenseAccounts },
      ignore: commonIgnore,
      date: { $gte: ninetyRange.start, $lte: ninetyRange.end }
    };

    // Expenses for 7/30
    const expenseQuery7 = {
      "source.account": { $in: this.expenseAccounts },
      ignore: commonIgnore,
      date: { $gte: sevenRange.start, $lte: sevenRange.end }
    };

    const expenseQuery30 = {
      "source.account": { $in: this.expenseAccounts },
      ignore: commonIgnore,
      date: { $gte: thirtyRange.start, $lte: thirtyRange.end }
    };

    this.transactionsService.find(revenueQuery7).subscribe((tx: Transaction[]) => {
      this.rollingRevenue7 = this.sumExAmounts(tx);
      this.rollingDesiredRevenue7 = this.sumDesiredRevenue(tx);
      this.transactionsService.find(expenseQuery7).subscribe((expTx: Transaction[]) => {
        this.rollingExpense7 = this.sumExAbsolute(expTx);
        this.rollingProfit7 = this.rollingRevenue7 - this.rollingExpense7;
        this.desiredProfit7 = this.rollingDesiredRevenue7 - this.rollingExpense7;
        this.desiredDelta7 = this.desiredProfit7 - this.rollingProfit7;
        this.expensesInRange = expTx || [];
        this.recentExpensesByCategory = this.groupExpensesByCategory(this.expensesInRange);
        this.resetExpenseSelection();
      });
    });

    this.transactionsService.find(revenueQuery30).subscribe((tx: Transaction[]) => {
      this.rollingRevenue30 = this.sumExAmounts(tx);
      this.rollingDesiredRevenue30 = this.sumDesiredRevenue(tx);
      this.transactionsService.find(expenseQuery30).subscribe((expTx: Transaction[]) => {
        this.rollingExpense30 = this.sumExAbsolute(expTx);
        this.rollingProfit30 = this.rollingRevenue30 - this.rollingExpense30;
        this.desiredProfit30 = this.rollingDesiredRevenue30 - this.rollingExpense30;
        this.desiredDelta30 = this.desiredProfit30 - this.rollingProfit30;
        if (this.selectedRangeDays === 30) {
          this.expensesInRange = expTx || [];
          this.recentExpensesByCategory = this.groupExpensesByCategory(this.expensesInRange);
          this.resetExpenseSelection();
        }
      });
    });

    // Net profit 90d: revenue minus expenses (all expense accounts)
    const revenueQuery90 = {
      "source.account": this.primaryRevenueAccount,
      ignore: commonIgnore,
      date: { $gte: ninetyRange.start, $lte: ninetyRange.end }
    };

    this.transactionsService.find(revenueQuery90).subscribe((revTx: Transaction[]) => {
      this.rollingRevenue90 = this.sumExAmounts(revTx);
      this.rollingDesiredRevenue90 = this.sumDesiredRevenue(revTx);
      this.transactionsService.find(expenseQuery90).subscribe((expTx: Transaction[]) => {
        this.rollingExpense90 = this.sumExAbsolute(expTx);
        this.rollingProfit90 = this.rollingRevenue90 - this.rollingExpense90;
        this.desiredProfit90 = this.rollingDesiredRevenue90 - this.rollingExpense90;
        this.desiredDelta90 = this.desiredProfit90 - this.rollingProfit90;
        this.buildProfitTrendFromTransactions(revTx, expTx);
        this.isLoadingMetrics = false;
        if (this.selectedRangeDays === 90) {
          this.expensesInRange = expTx || [];
          this.recentExpensesByCategory = this.groupExpensesByCategory(this.expensesInRange);
          this.resetExpenseSelection();
        }
      }, () => { this.isLoadingMetrics = false; });
    }, () => { this.isLoadingMetrics = false; });

  }

  private exGstAmount(amount: number): number {
    if (amount == null) return 0;
    return amount - amount / 11;
  }

  private exAmount(tx: Transaction): number {
    const amt = tx?.amount || 0;
    const vat = tx?.vat || 0;
    return amt - vat;
  }

  private sumExAmounts(transactions: Transaction[]): number {
    return (transactions || []).reduce((sum, t) => sum + this.exAmount(t), 0);
  }

  private sumDesiredRevenue(transactions: Transaction[]): number {
    return (transactions || []).reduce((sum, t) => {
      const desired = t.additional?.desiredInvoiceAmount;
      const ex = this.exAmount(t);
      // desiredInvoiceAmount stored as cents; if absent fall back to ex amount
      const val = typeof desired === 'number' ? desired : ex;
      return sum + val;
    }, 0);
  }

  private sumExAbsolute(transactions: Transaction[]): number {
    return (transactions || []).reduce((sum, t) => sum + Math.abs(this.exAmount(t)), 0);
  }

  displayExAmount(tx: Transaction): number {
    return this.exAmount(tx);
  }

  private getRollingRange(days: number, now: Date = new Date()): { start: Date; end: Date } {
    const end = new Date(now);
    end.setHours(23, 59, 59, 999); // include today through end of day
    const start = new Date(end);
    start.setDate(end.getDate() - (days - 1));
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }

  get currentRevenueTotal(): number {
    switch (this.selectedRangeDays) {
      case 30: return this.rollingRevenue30;
      case 90: return this.rollingRevenue90;
      default: return this.rollingRevenue7;
    }
  }

  get currentExpenseTotal(): number {
    switch (this.selectedRangeDays) {
      case 30: return this.rollingExpense30;
      case 90: return this.rollingExpense90;
      default: return this.rollingExpense7;
    }
  }

  selectRange(days: number) {
   if (!this.rangeOptions.includes(days)) return;
   this.selectedRangeDays = days;
   this.loadRangeLists(days);
 }

  private loadRangeLists(days: number, preserveSelection: boolean = false) {
    const now = new Date();
    const { start, end } = this.getRollingRange(days, now);
    const commonIgnore = { $in: [null, false] };

    const revenueQuery = {
      "source.account": this.primaryRevenueAccount,
      ignore: commonIgnore,
      date: { $gte: start, $lte: end }
    };
    this.transactionsService.find(revenueQuery, 0, 50, { date: -1 }).subscribe((tx: Transaction[]) => {
      this.recentRevenue7 = tx || [];
    });

    const expenseQuery = {
      "source.account": { $in: this.expenseAccounts },
      ignore: commonIgnore,
      date: { $gte: start, $lte: end }
    };
    this.transactionsService.find(expenseQuery, 0, 200, { date: -1 }).subscribe((tx: Transaction[]) => {
      this.expensesInRange = tx || [];
      this.recentExpensesByCategory = this.groupExpensesByCategory(this.expensesInRange);
      if (preserveSelection && this.selectedExpenseCategory) {
        const match = this.recentExpensesByCategory.find(c => c.category === this.selectedExpenseCategory?.category);
        if (match) {
          this.selectExpenseCategory(match);
        } else {
          this.resetExpenseSelection();
        }
      } else {
        this.resetExpenseSelection();
      }
    });
  }

  openReceipt(tx: Transaction, kind: 'rev' | 'exp') {
    if (!tx) return;
    const list = kind === 'exp' ? this.selectedExpenseTransactions : this.recentRevenue7;
    const dataSource = new MatTableDataSource<Transaction>(list);
    const index = list.findIndex(t => t.source?.sha1 === tx.source?.sha1);

    this.transactionsService.openTransactionDialog(
      this.transactionsService.getTransactionType(tx),
      tx.source.sha1,
      index >= 0 ? index : 0,
      dataSource,
      () => {
        // gentle refresh: preserve current expense selection if present
        this.loadRangeLists(this.selectedRangeDays, true);
      }
    );
  }

  private groupExpensesByCategory(expenses: Transaction[]): ExpenseCategoryRow[] {
    const buckets: Record<string, ExpenseCategoryRow> = {};
    for (const tx of expenses) {
      const cat = tx.atocategory || 'uncategorised';
      const bucket = buckets[cat] || { category: cat, label: this.getCategoryLabel(cat), amount: 0, count: 0 };
      bucket.amount += this.exAmount(tx);
      bucket.count += 1;
      buckets[cat] = bucket;
    }
    return Object.values(buckets).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)).reverse();
  }

  private getCategoryLabel(category: string): string {
    if (category === 'uncategorised') return 'Uncategorised';
    const findLabel = (arr: { label: string; value: string }[]) =>
      arr.find(c => c.value === category)?.label;
    return findLabel(COGS) || findLabel(OperatingExpenses) || category;
  }

  private formatCentsToDollars(val: number, withSymbol = true): string {
    const dollars = (val || 0) / 100;
    return `${withSymbol ? '$' : ''}${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private linearSlope(values: number[]): number {
    const n = values.length;
    if (n < 2) return 0;
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (let i = 0; i < n; i++) {
      const x = i;
      const y = values[i];
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumXX += x * x;
    }
    const denom = n * sumXX - sumX * sumX;
    if (denom === 0) return 0;
    return (n * sumXY - sumX * sumY) / denom;
  }

  private buildProfitTrendFromTransactions(revTx: Transaction[], expTx: Transaction[]) {
    const byDate: Record<string, { rev: number; exp: number }> = {};
    const range = this.getRollingRange(90);

    // seed missing days with 0 to keep chart continuous
    for (let d = new Date(range.start); d <= range.end; d.setDate(d.getDate() + 1)) {
      const key = new Date(d).toISOString().substring(0, 10);
      byDate[key] = { rev: 0, exp: 0 };
    }

    (revTx || []).forEach(tx => {
      if (!tx?.date) return;
      const d = new Date(tx.date);
      const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().substring(0, 10);
      const entry = byDate[key] || { rev: 0, exp: 0 };
      entry.rev += this.exAmount(tx);
      byDate[key] = entry;
    });

    (expTx || []).forEach(tx => {
      if (!tx?.date) return;
      const d = new Date(tx.date);
      const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().substring(0, 10);
      const entry = byDate[key] || { rev: 0, exp: 0 };
      entry.exp += Math.abs(this.exAmount(tx));
      byDate[key] = entry;
    });

    const dates = Object.keys(byDate).sort();
    const seriesData = dates.map(key => {
      const entry = byDate[key];
      return { x: new Date(key), y: entry.rev - entry.exp };
    });

    // Compute linear regression slope for y over index
    const yValues = seriesData.map(pt => pt.y);
    const slope = this.linearSlope(yValues);
    this.profitSlopePerDay = slope;
    const n = seriesData.length;
    const meanX = (n - 1) / 2;
    const meanY = yValues.reduce((s, v) => s + v, 0) / (n || 1);
    const intercept = meanY - slope * meanX;

    const trendLine = seriesData.map((pt, idx) => ({
      x: pt.x,
      y: slope * idx + intercept
    }));
    this.lastTrendValue = trendLine.length ? trendLine[trendLine.length - 1].y : 0;
    this.lastTrendValue = trendLine.length ? trendLine[trendLine.length - 1].y : 0;

    // rolling 90-day average profit per day
    const window: number[] = [];
    let windowSum = 0;
    const windowSize = 90;
    const averageSeries = seriesData.map((pt) => {
      window.push(pt.y);
      windowSum += pt.y;
      if (window.length > windowSize) {
        windowSum -= window.shift() as number;
      }
      return { x: pt.x, y: windowSum / window.length };
    });

    const avg = seriesData.length
      ? seriesData.reduce((sum, pt) => sum + pt.y, 0) / seriesData.length
      : 0;
    this.avgProfitPerDay = avg / 100; // dollars per day

    // establish base y-range for zooming (include goal lines)
    const survival = 8500;
    const dignity = 14000;
    const dataMax = yValues.length ? Math.max(...yValues, dignity) : dignity;
    const dataMin = yValues.length ? Math.min(...yValues, survival, 0) : 0;
    const pad = Math.max(1000, (dataMax - dataMin) * 0.15);
    this.yZoomMin = dataMin - pad;
    this.yZoomMax = dataMax + pad;

    this.lastAverageSeries = averageSeries;
    this.lastTrendLine = trendLine;

    this.trendChartOptions = {
      series: this.showRollingAverage
        ? [
            { name: 'Avg to date', data: averageSeries, color: '#10b981' },
            { name: 'Trend', data: trendLine, color: '#2563eb' }
          ]
        : [
            { name: 'Trend', data: trendLine, color: '#2563eb' }
          ],
      chart: {
        type: 'line',
        height: 320,
        toolbar: { show: false },
        zoom: {
          enabled: true,
          type: 'y',
          zoomedArea: {
            fill: { color: '#dbeafe', opacity: 0.2 },
            stroke: { color: '#2563eb', opacity: 0.4, width: 1 }
          }
        }
      },
      stroke: {
        curve: 'straight',
        width: this.showRollingAverage ? [2, 3] : [3],
        dashArray: this.showRollingAverage ? [4, 0] : [0]
      },
      markers: {
        size: [0, 0],
        strokeWidth: 0,
        hover: {
          size: 6,
          sizeOffset: 3
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        type: 'datetime',
        labels: { format: 'dd MMM' }
      },
      yaxis: {
        min: this.yZoomMin,
        max: this.yZoomMax,
        labels: {
          formatter: (val: number) => this.formatCentsToDollars(val)
        }
      },
      tooltip: {
        shared: true,
        x: { format: 'dd MMM' },
        y: {
          formatter: (val: number) => this.formatCentsToDollars(val)
        }
      },
      annotations: {
        yaxis: [
          {
            y: avg,
            borderColor: '#6366f1',
            label: {
              text: '90d avg',
              style: { background: '#6366f1', color: '#fff' }
            }
          },
          {
            y: 8500, // survival line ($85)
            borderColor: '#10b981',
            strokeDashArray: 0,
            label: {
              text: 'Survival $85',
              style: { background: '#10b981', color: '#fff' }
            }
          },
          {
            y: 14000, // dignity goal ($140)
            borderColor: '#f59e0b',
            strokeDashArray: 0,
            label: {
              text: 'Dignity $140',
              style: { background: '#f59e0b', color: '#fff' }
            }
          }
        ]
      }
    };

    if (!this.appliedInitialZoom) {
      this.appliedInitialZoom = true;
      this.zoomToFit();
    }
  }
}

interface ExpenseCategoryRow {
  category: string;
  label: string;
  amount: number;
  count: number;
}


